/* eslint-disable no-undef */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Spinner, Alert } from 'components/base';
import ScriptLoader from 'react-script-loader-hoc';
import sleep from 'utils/sleep';

import SETTINGS from '../settings';
import amazonApi, {
  AMAZON_PAY_V2_SCRIPT_URL,
  AMAZON_PAY_V2_SIGNATURE_ALGORITHM,
} from '../api/amazon';

// How long the button waits for window.amazon.Pay before it reports Amazon Pay
// as unavailable.
const AMAZON_PAY_READY_POLL_MS = 300;
const AMAZON_PAY_READY_TIMEOUT_MS = 10000;

// Strips the buyerToken query param from the URL after we've consumed it, so a
// page refresh doesn't re-trigger the redeem call. Preserves any other query
// params the host page cares about (e.g. ?redirect=…).
const stripBuyerTokenFromUrl = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete('buyerToken');
  url.searchParams.delete('amazonCheckoutSessionId');
  window.history.replaceState({}, '', url.toString());
};

const SignInWithAmazonV2Inner = ({
  scriptsLoadedSuccessfully,
  redirectUrl,
  cancelUrl,
  className,
  accessToken,
  countryCode,
  onLoading,
  onSuccess,
  onFailed,
}) => {
  const buttonElementId = 'amazon-signin-button';
  const buttonRef = useRef();
  const [isAvailable, setIsAvailable] = useState(true);
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [signature, setSignature] = useState();

  // Stringify once — same bytes signed on the backend and handed to Amazon as
  // `payloadJSON`. Ruby re-serialization would produce different output and
  // break signature verification.
  const payloadJSON = useMemo(
    () =>
      JSON.stringify({
        signInReturnUrl: `${SETTINGS.HOST}${redirectUrl}`,
        signInCancelUrl: `${SETTINGS.HOST}${cancelUrl || redirectUrl}`,
        storeId: SETTINGS.AMAZON_PAY_V2_STORE_ID,
        signInScopes: ['name', 'email'],
      }),
    [redirectUrl, cancelUrl],
  );

  // Step 1: if Amazon redirected us back with ?buyerToken=…, redeem it.
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const buyerToken = params.get('buyerToken');
    if (!buyerToken) {
      return;
    }

    setIsRedeeming(true);
    onLoading({ buyerToken });

    (async () => {
      const { data, error } = await amazonApi(accessToken).redeemBuyerToken(
        buyerToken,
        countryCode,
      );
      stripBuyerTokenFromUrl();

      if (data) {
        await onSuccess(data);
      } else {
        onFailed(error || 'unavailable');
      }
      setIsRedeeming(false);
    })();
    // We intentionally only want to run this once per mount.
  }, []); // eslint-disable-line

  // Step 2: ask the backend to sign the SignIn payload.
  useEffect(() => {
    if (isRedeeming) {
      return;
    }
    (async () => {
      const { data } = await amazonApi(accessToken).signSignInPayload(payloadJSON);
      if (data?.signature) {
        setSignature(data.signature);
      }
    })();
  }, [payloadJSON, isRedeeming]);

  // Step 3: render the Amazon Pay button (productType: 'SignIn').
  useEffect(() => {
    if (!scriptsLoadedSuccessfully || isRedeeming || !signature) {
      return;
    }

    let cancelled = false;
    (async () => {
      // The Pay namespace can lag the script-loaded event. On a remount the script
      // loader reports success while the first mount's script is still loading,
      // so wait for the namespace instead of checking once.
      let waited = 0;
      do {
        await sleep(AMAZON_PAY_READY_POLL_MS);
        if (cancelled) return;
        waited += AMAZON_PAY_READY_POLL_MS;
      } while (!window.amazon?.Pay && waited < AMAZON_PAY_READY_TIMEOUT_MS);

      if (!window.amazon?.Pay || !document.getElementById(buttonElementId)) {
        setIsAvailable(false);
        return;
      }

      try {
        buttonRef.current = amazon.Pay.renderButton(`#${buttonElementId}`, {
          merchantId: SETTINGS.AMAZON_PAY_SELLER_ID,
          publicKeyId: SETTINGS.AMAZON_PAY_V2_PUBLIC_KEY_ID,
          ledgerCurrency: 'USD',
          checkoutLanguage: 'en_US',
          productType: 'SignIn',
          placement: 'Other',
          buttonColor: 'Gold',
          signInConfig: {
            payloadJSON,
            signature,
            algorithm: AMAZON_PAY_V2_SIGNATURE_ALGORITHM,
          },
        });
      } catch (e) {
        console.error(e);
        setIsAvailable(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [scriptsLoadedSuccessfully, signature, isRedeeming, payloadJSON]);

  return (
    <div className={className}>
      <div style={{ height: 46 }}>
        <Spinner theme="warning" className={isRedeeming ? 'd-inline-block' : 'd-none'} />
        <div
          id={buttonElementId}
          className={isRedeeming || !isAvailable || !signature ? 'd-none' : 'd-block'}
        />
        {!isAvailable && <Alert type="warning">Amazon Pay is temporarily unavailable</Alert>}
      </div>
    </div>
  );
};

SignInWithAmazonV2Inner.propTypes = {
  scriptsLoadedSuccessfully: PropTypes.bool,
  redirectUrl: PropTypes.string,
  cancelUrl: PropTypes.string,
  className: PropTypes.string,
  accessToken: PropTypes.string,
  countryCode: PropTypes.string,
  onLoading: PropTypes.func,
  onSuccess: PropTypes.func,
  onFailed: PropTypes.func,
};

const noop = () => undefined;

SignInWithAmazonV2Inner.defaultProps = {
  redirectUrl: '/signin',
  onLoading: noop,
  onSuccess: noop,
  onFailed: console.error,
};

const SignInWithAmazonV2 = ScriptLoader(AMAZON_PAY_V2_SCRIPT_URL)(SignInWithAmazonV2Inner);

export default SignInWithAmazonV2;
